import { memo, useState } from "react";
import type { GoldenSerenityTemplateProps, Translator } from "./golden-serenity/types";
import { isRsvpCutoffPassed } from "./golden-serenity/utils";
import { BokehBackground } from "./golden-serenity/decorations";
import { GoldenSerenityNavbar } from "./golden-serenity/Navbar";
import { GoldenSerenityHero } from "./golden-serenity/Hero";
import { GoldenSerenityCouplePhoto } from "./golden-serenity/CouplePhoto";
import { GoldenSerenityCountdown } from "./golden-serenity/Countdown";
import { GoldenSerenityOurStory } from "./golden-serenity/OurStory";
import { GoldenSerenityGallery } from "./golden-serenity/Gallery";
import { GoldenSerenityGodparents, GoldenSerenityParents } from "./golden-serenity/Family";
import { GoldenSerenityDetails } from "./golden-serenity/Details";
import { GoldenSerenityRSVP } from "./golden-serenity/RSVP";
import { GoldenSerenitySaveTheDate } from "./golden-serenity/SaveTheDate";
import { GoldenSerenityRegistry } from "./golden-serenity/Registry";
import { GoldenSerenityFooter } from "./golden-serenity/Footer";
import { PublicRsvpForm } from "@/components/public/PublicRsvpForm";

/**
 * Golden Serenity — elegant gold/serene template adapted from the Figma
 * "Design 11 Jubiel Golden Serenity" prototype (Lora / Nunito Sans, warm
 * cream backgrounds, botanical gold-and-sage accents).
 */
export const GoldenSerenityTemplate = memo((props: GoldenSerenityTemplateProps) => {
  const { event, config, locale, t: originalT } = props;
  const [rsvpDialogOpen, setRsvpDialogOpen] = useState(false);

  // Ensure t is always a function — provide fallback if undefined.
  const t: Translator =
    typeof originalT === "function"
      ? originalT
      : ((key: string, params?: Record<string, string | number | undefined>) => {
          const defaultValue = params?.defaultValue;
          return typeof defaultValue === "string" ? defaultValue : String(defaultValue ?? key);
        });

  const sectionProps = { event, config, locale, t };
  const openRsvpDialog = () => setRsvpDialogOpen(true);

  return (
    <div
      className="relative flex flex-col w-full min-h-screen overflow-x-hidden"
      style={{ background: config.palette.background, fontFamily: config.typography.body }}
    >
      {/* Template-scoped keyframes; disabled under prefers-reduced-motion. */}
      <style>{`
        @keyframes gs-float {
          0% { transform: translateY(0px); }
          100% { transform: translateY(-20px); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes gs-float {
            0% { transform: none; }
            100% { transform: none; }
          }
        }
      `}</style>

      <BokehBackground />

      <div className="relative z-10">
        <GoldenSerenityNavbar {...props} t={t} onRsvpClick={openRsvpDialog} />
        <GoldenSerenityHero {...props} t={t} onRsvpClick={openRsvpDialog} />
        <GoldenSerenityCouplePhoto {...sectionProps} />
        <GoldenSerenityCountdown {...sectionProps} />
        <GoldenSerenityOurStory {...sectionProps} />
        <GoldenSerenityGallery {...sectionProps} />
        <GoldenSerenityGodparents {...sectionProps} />
        <GoldenSerenityParents {...sectionProps} />
        <GoldenSerenityDetails {...sectionProps} />
        <GoldenSerenityRSVP {...props} t={t} onRsvpClick={openRsvpDialog} />
        <GoldenSerenitySaveTheDate {...sectionProps} onShare={props.onShare} />
        <GoldenSerenityRegistry {...sectionProps} />
        <GoldenSerenityFooter {...sectionProps} />
      </div>

      {/* RSVP form dialog */}
      {event.rsvpSettings?.rsvpEnabled && !isRsvpCutoffPassed(event) && (
        <PublicRsvpForm
          event={event}
          config={config}
          locale={locale}
          open={rsvpDialogOpen}
          onOpenChange={setRsvpDialogOpen}
        />
      )}
    </div>
  );
});

GoldenSerenityTemplate.displayName = "GoldenSerenityTemplate";
